import { useEffect } from 'react';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import {
  QuizConfigProvider,
  useQuizMetaContext,
  useQuizResults,
  useSocketIoConnection,
} from '@mentimeter/quiz';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { FollowerPresentation } from './FollowerPresentation';
import { LivePresentation } from './LivePresentation';
import { useLeaderboardResults } from './useLeaderboardResults';
import { LEADERBOARD_PREVIEW_RESULT } from './constants';
import type { QuizLeaderboardPresentationProps } from './types';

interface LivePresentationDataHandlerProps
  extends QuizLeaderboardPresentationProps {
  slideAdminKey: string;
}

export const LivePresentationDataHandler = ({
  title,
  styledTitle,
  label,
  description,
  textSizeOffset,
  slidePublicKey,
  slideAdminKey,
  timeBasedScoring,
}: LivePresentationDataHandlerProps) => {
  const {
    data: results,
    mutate,
    error,
  } = useQuizResults<QuizPlayersScoreData>();
  const {
    useCurrentSession,
    useConfig,
    useVotingInstructions,
    isPreviewResultsEnabled,
  } = usePresentationContext();
  const { voteKey } = useVotingInstructions();

  const { currentSessionId: session } = useCurrentSession();
  const { isLastLeaderboard, follower } = useQuizMetaContext();
  const { connection } = useSocketIoConnection();
  const { enableAnimations } = useConfig();

  const leaderboardResults = useLeaderboardResults(
    isPreviewResultsEnabled ? LEADERBOARD_PREVIEW_RESULT : results,
  );

  // Invalidate results when session is updated
  useEffect(() => {
    return () => void mutate(undefined);
  }, [session, mutate]);

  if (error) {
    return <Error errorCode={ClientErrorCodes.RESULTS_FETCH} />;
  }

  if (!leaderboardResults || !connection || session === undefined) {
    return <Loading />;
  }

  if (follower) {
    return (
      <QuizConfigProvider
        questionPublicKey={slidePublicKey}
        questionAdminKey={slideAdminKey}
        voteKey={voteKey}
      >
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          description={description}
          fontSizeOffset={textSizeOffset}
        />
        <CenteringContainer>
          <FollowerPresentation
            timeBasedScoring={timeBasedScoring}
            leaderboardResults={leaderboardResults}
            shouldAnimate={enableAnimations}
            mutateResults={mutate}
            slidePublicKey={slidePublicKey}
            initialGameState={
              isLastLeaderboard
                ? GameStateEnums.FINAL_LEADERBOARD
                : GameStateEnums.LEADERBOARD
            }
            isPreviewResults={isPreviewResultsEnabled}
          />
        </CenteringContainer>
      </QuizConfigProvider>
    );
  }

  return (
    <QuizConfigProvider
      questionPublicKey={slidePublicKey}
      questionAdminKey={slideAdminKey}
      voteKey={voteKey}
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        label={label}
        description={description}
        fontSizeOffset={textSizeOffset}
      />
      <CenteringContainer>
        <LivePresentation
          activeSession={session}
          timeBasedScoring={timeBasedScoring}
          initialGameState={
            isLastLeaderboard
              ? GameStateEnums.FINAL_LEADERBOARD
              : GameStateEnums.LEADERBOARD
          }
          leaderboardResults={leaderboardResults}
          shouldAnimate={enableAnimations}
          questionId={slideAdminKey}
          isPreviewResults={isPreviewResultsEnabled}
        />
      </CenteringContainer>
    </QuizConfigProvider>
  );
};
